import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
// Assets
import { createRoverTdm } from '@/api/Rover/applicationTDM';
import { DueDateInput } from '@/components/Projects/Project/DueDateInput';
import { SupportSelect } from '@/components/Projects/Project/SupportSelect';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
// Custom UI
import Loading from '@/components/UI/loading';
import { TdmReactQueryKey } from '@/constants/ReactQueryKeys';
import { useSherpaVendors } from '@/hooks/ReactQueryHooks/useVendorQuery';
import { notifyUser } from '@/store/Toast';
import type { UserOption } from '@/types/Constants';
import { sanitizeResponseError } from '@/utils';
import styles from '../Form.module.css';
import { validationSchema } from './validationSchema';

export default function NewMilestoneForm({
  phase,
  teams,
  formModalHandler,
}: {
  phase: { label: string; value: string };
  teams?: any;
  formModalHandler: (name: string) => void;
}) {
  const params = useParams<string>();
  const dispatch = useDispatch();
  const initialData = {
    name: '',
    notes: '',
    lead: 0,
    support: 0,
    vendor: 0,
    startDate: '',
    dueDate: '',
  };
  const queryClient = useQueryClient();
  const handleAddSuccess = useCallback(() => {
    queryClient.invalidateQueries([TdmReactQueryKey.GET_APPLICATION_MILESTONES]);
    formModalHandler('newMilestone');
    dispatch(
      notifyUser({
        showToast: true,
        toastMessage: 'Milestone created successfully',
        toastType: 'success',
      })
    );
  }, [queryClient, dispatch, formModalHandler]);

  const fetchVendors = useSherpaVendors({});
  const vendors = fetchVendors?.data?.data;

  const addMilestone = useMutation((payload: any) => createRoverTdm({ type: 'milestones', payload }), {
    onSuccess: () => {
      handleAddSuccess();
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });
  const onSubmit = async (values: any) => {
    const selectedData = {
      name: values.name,
      phase: phase.value,
      leadId: Number(values.lead.id),
      ...(values.notes && { notes: values.notes }),
      ...(values.support && { supportId: Number(values.support.id) }),
      ...(values.vendor && { vendorId: Number(values.vendor.id) }),
      ...(values.startDate && {
        startDate: dayjs(values.startDate).utc().format('MM/DD/YYYY'),
      }),
      ...(values.dueDate && {
        dueDate: dayjs(values.dueDate).utc().format('MM/DD/YYYY'),
      }),
      applicationId: params.applicationId,
    };
    addMilestone.mutate(selectedData);
  };
  return (
    <HookFormProvider defaultValues={initialData} resolver={yupResolver(validationSchema) as any} mode='all'>
      <HookForm noDefaultClass className='w-full' onSubmit={onSubmit}>
        {addMilestone.isLoading && <Loading loadingText='Updating Project' />}
        <h2 className={styles['form-title']}>New {phase.label} Milestone</h2>
        <div className='grid grid-cols-2 gap-5'>
          <HookInput type='text' label='Milestone Name' name='name' id='name' placeholder='e.g. SEIN Office' />
          <HookReactSingleSelect
            id='lead'
            options={teams}
            getOptionLabel={(option) => (option as UserOption).name}
            getOptionValue={(option) => (option as UserOption).id.toString()}
            placeholder='Select Lead'
            name='lead'
            label='Lead'
          />
          <HookInput type='date' label='Start Date' name='startDate' id='due-date' />
          <SupportSelect internalTeam={teams} />
          <DueDateInput />
          <HookReactSingleSelect
            id='vendor'
            options={vendors}
            getOptionLabel={(option) => (option as UserOption).name}
            getOptionValue={(option) => (option as UserOption).id.toString()}
            placeholder='Select Vendor'
            name='vendor'
            label='Vendor'
            isClearable
          />

          <div className='col-span-2'>
            <HookTextarea rows={3} id='notes' label='Notes' name='notes' placeholder='e.g. any example' />
          </div>
        </div>
        <div className='flex gap-4 justify-end mt-4'>
          <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={() => formModalHandler('newMilestone')} />
          <Button label='Create' btnStyle='btn-primary' type='submit' />
        </div>
      </HookForm>
    </HookFormProvider>
  );
}
